import { Vendor } from '../../clients/quipli-service/vendor/vendor';
import { useState } from 'react';
import { Field } from '../../components/field/field';
import { Phone } from '../../components/field/phone';
import { Modal } from '../../components/modal/modal';
import { Spinner } from '../../components/spinner/spinner';
import { Button } from '../../components/form/button';
import { useVendorApi } from '../../clients/quipli-service/vendor/vendor-api';
import { toast } from '../../util/toasts';

export const VendorForm = ({
  type = 'inline',
  vendor,
  onSubmit,
  onClose,
}: {
  type?: 'inline' | 'modal';
  vendor?: Vendor;
  onSubmit: (vendor: Vendor) => Promise<void>;
  onClose?: () => void;
}) => {
  const { createVendor, updateVendor } = useVendorApi();

  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [name, setName] = useState<string>(vendor?.name || '');
  const [phone, setPhone] = useState<string>(vendor?.phone || '');
  const [email, setEmail] = useState<string>(vendor?.email || '');
  const isDisabled = name.length === 0;

  const handleSubmit = async () => {
    if (isRequestInProgress || isDisabled) {
      return;
    }

    setRequestInProgress(true);

    try {
      const req = { name, phone, email };

      const res = await (vendor ? updateVendor(vendor.id, req) : createVendor(req));
      await onSubmit(res);
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to save vendor', 'error');
    } finally {
      setRequestInProgress(false);
    }
  };

  return type === 'inline' ? (
    <div className="flex flex-col gap-3">
      <VendorFormWrapper
        name={name}
        email={email}
        phone={phone}
        setName={setName}
        setEmail={setEmail}
        setPhone={setPhone}
      />

      <div className={'flex justify-end'}>
        <Button className="button button-small button-block button-yellow" disabled={isDisabled} onClick={handleSubmit}>
          {isRequestInProgress && <Spinner />} Save
        </Button>
      </div>
    </div>
  ) : (
    <Modal
      isOpen
      close={() => {
        if (isRequestInProgress) return;
        onClose?.();
      }}
      title="Create Vendor"
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <Button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              className="button button-small button-block button-yellow"
              disabled={isDisabled}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </>
        )
      }
    >
      <VendorFormWrapper
        name={name}
        email={email}
        phone={phone}
        setName={setName}
        setEmail={setEmail}
        setPhone={setPhone}
      />
    </Modal>
  );
};

const VendorFormWrapper = ({
  name,
  email,
  phone,
  setName,
  setEmail,
  setPhone,
}: {
  name: string;
  email: string;
  phone: string;
  setName: (v: string) => void;
  setEmail: (v: string) => void;
  setPhone: (v: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-3">
      <Field
        type="text"
        label="Name"
        dataCy="name"
        value={name}
        onChange={(name: string) => setName(name)}
        isRequired={true}
      />

      <Field type="text" label="Email" dataCy="email" value={email} onChange={(email: string) => setEmail(email)} />

      <Phone className="w-full" value={phone} onChange={(phone) => setPhone(phone)} onChangeValidity={() => {}} />
    </div>
  );
};
